import React from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { TextField, RadioGroup } from 'formik-material-ui';
import ReactMarkdown from 'react-markdown';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import DialogPaper from '../DialogPaper';

import * as SurveyService from '../../api/surveyService';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.common.white,
    color: '#000000',
    marginBottom: theme.spacing(6),
  },
  buttonContainer: {
    width: 100,
    margin: theme.spacing(),
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  textField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    paddingLeft: 12,
    paddingRight: 12,
    minHeight: 30,
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  boxTerm: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    maxHeight: 200,
    overflow: 'auto',
    marginTop: theme.spacing(2),
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  description: {
    textAlign: 'center',
    marginBottom: theme.spacing(),
  },
  terms: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  merchantLogo: {
    height: 120,
    width: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  logoContainer: {
    marginBottom: theme.spacing(),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  markdown: {
    fontFamily: 'ClarikaGeometric',
    fontSize: 14,
    fontWeight: 400,
    textAlign: 'center',
  },
  markdownTerm: {
    fontFamily: 'ClarikaGeometric',
    fontSize: 10,
    fontWeight: 400,
  },
  error: {
    color: theme.palette.error.main,
  },
  questionTitle: {
    marginTop: theme.spacing(),
    fontWeight: theme.typography.fontWeightBold,
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  rewardId: string;
  survey: SurveyService.Survey;
  confirmUnlock: (status: boolean) => void;
  logo: string;
  companyId: string;
};

export default function SurveyDialog({
  rewardId,
  survey,
  onClose,
  open,
  confirmUnlock,
  logo,
  companyId,
}: Props) {
  const regex = /^$|\s+$/;
  let initValues: any = {};
  survey.questions.forEach((q, idx) => {
    initValues[`question${idx + 1}`] = '';
  });

  const classes = useStyles();

  const handleSubmit = async (
    values: typeof initValues,
    { setFieldError, setSubmitting }: FormikHelpers<typeof initValues>
  ) => {
    setSubmitting(true);
    let updatedAnswer = survey.questions.map((q, idx) => {
      return {
        question: q.question,
        answer: values[`question${idx + 1}`] as string,
      };
    });
    try {
      await SurveyService.createCompletedSurvey(
        rewardId,
        survey.id,
        companyId,
        updatedAnswer
      );
      confirmUnlock(true);
      setSubmitting(false);
    } catch (e: any) {
      console.log('e', e);
      confirmUnlock(false);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  const validateField = (value: string) => {
    let errorMessage;
    if (regex.test(value)) {
      console.log('Field Required');
      errorMessage = 'Required';
    }
    return errorMessage;
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <Formik initialValues={initValues} onSubmit={handleSubmit}>
        {({ isSubmitting, dirty, errors, touched }) => {
          return (
            <Form noValidate>
              <Toolbar className={classes.toolbar}>
                <div className={classes.box}>
                  <div className={classes.close}>
                    <IconButton onClick={handleClose}>
                      <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                  </div>
                </div>
                <div className={classes.box}>
                  <div className={classes.actions}></div>
                </div>
              </Toolbar>
              <div className={classes.logoContainer}>
                <img
                  src={logo}
                  className={classes.merchantLogo}
                  alt="Merchant Logo"
                />
              </div>
              <Container maxWidth="md">
                <div className={classes.cardContainer}>
                  <div className={classes.box}>
                    <ReactMarkdown className={classes.markdown}>
                      {survey.description}
                    </ReactMarkdown>
                  </div>
                  {survey.questions && survey.questions.length > 0 && (
                    <>
                      {survey.questions.map((q, idx) => (
                        <>
                          {q.questionType === 'text' && (
                            <>
                              {q.required ? (
                                <>
                                  <Typography
                                    variant="body2"
                                    className={classes.questionTitle}
                                  >
                                    {q.question}
                                  </Typography>
                                  <Field
                                    key={`text${idx}`}
                                    component={TextField}
                                    className={classes.textField}
                                    name={`question${idx + 1}`}
                                    autoFocus
                                    variant="standard"
                                    validate={validateField}
                                  />
                                </>
                              ) : (
                                <>
                                  <Typography
                                    variant="body2"
                                    className={classes.questionTitle}
                                  >
                                    {q.question}
                                  </Typography>
                                  <Field
                                    key={`text${idx}`}
                                    component={TextField}
                                    className={classes.textField}
                                    name={`question${idx + 1}`}
                                    autoFocus
                                    variant="standard"
                                  />
                                </>
                              )}
                            </>
                          )}
                          {q.questionType === 'multiple' && (
                            <>
                              <Typography
                                variant="body2"
                                className={classes.questionTitle}
                              >
                                {q.question}
                              </Typography>
                              {q.required ? (
                                <Field
                                  key={`radio${idx}`}
                                  component={RadioGroup}
                                  type="radio"
                                  name={`question${idx + 1}`}
                                  validate={validateField}
                                >
                                  {q.options.Options.map((op, i) => (
                                    <div
                                      key={idx}
                                      className={classes.radioContainer}
                                    >
                                      <FormControlLabel
                                        value={op.option}
                                        control={<Radio color="primary" />}
                                        label=""
                                      />
                                      <div key={`option${i}`}>
                                        <Typography
                                          variant="body2"
                                          className={classes.title}
                                        >
                                          {op.option}
                                        </Typography>
                                      </div>
                                    </div>
                                  ))}
                                  {errors[`question${idx + 1}`] &&
                                  touched[`question${idx + 1}`] ? (
                                    <div className={classes.error}>
                                      {errors[`question${idx + 1}`]}
                                    </div>
                                  ) : null}
                                </Field>
                              ) : (
                                <Field
                                  key={`radio${idx}`}
                                  component={RadioGroup}
                                  type="radio"
                                  name={`question${idx + 1}`}
                                >
                                  {q.options.Options.map((op, i) => (
                                    <div
                                      key={idx}
                                      className={classes.radioContainer}
                                    >
                                      <FormControlLabel
                                        value={op.option}
                                        control={<Radio color="primary" />}
                                        label=""
                                      />
                                      <div key={`option${i}`}>
                                        <Typography
                                          variant="body2"
                                          className={classes.title}
                                        >
                                          {op.option}
                                        </Typography>
                                      </div>
                                    </div>
                                  ))}
                                  {errors[`question${idx + 1}`] &&
                                  touched[`question${idx + 1}`] ? (
                                    <div className={classes.error}>
                                      {errors[`question${idx + 1}`]}
                                    </div>
                                  ) : null}
                                </Field>
                              )}
                            </>
                          )}
                        </>
                      ))}
                    </>
                  )}
                  <div className={classes.actionContainer}>
                    <Button
                      type="submit"
                      color="primary"
                      className={classes.buttonContainer}
                      disabled={isSubmitting}
                    >
                      Submit
                    </Button>
                  </div>
                  <div className={classes.boxTerm}>
                    <ReactMarkdown className={classes.markdownTerm}>
                      {survey.terms}
                    </ReactMarkdown>
                  </div>
                </div>
              </Container>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
